<template>
  <div class="wrapper wrapper-full-page">
    <div class="full-page login-page page-content">
      <div class="content">
        <div class="container">
          <el-form
            v-loading="status.loading"
            class="login-form"
            element-loading-text="Checking Authorization..."
            element-loading-custom-class="spinner-class"
            @submit.prevent="login"
          >
            <div class="login-form__header">
              <div class="login-form__header--logo" />
              <h1 class="login-form__header--title">
                Log in
              </h1>
            </div>

            <div class="login-form__content">
              <div class="mb-20 mobile-mb-10">
                <label for="email">Email Address</label>
                <input
                  id="email"
                  v-model="user.email"
                  type="email"
                  data-test="input-email"
                  class="form-control input-no-border"
                  :pattern="emailPattern"
                  required
                >
              </div>

              <div>
                <label for="password">Password</label>
                <password-field
                  id="password"
                  v-model="user.password"
                  :disable-autocomplete="false"
                />
              </div>
              <message-html
                class="mt-20"
                :result-html="status.resultHtml"
                :has-top-line="false"
              />
            </div>

            <div class="login-form__footer pt-10">
              <p-button
                data-test="btn-login"
                native-type="submit"
                type="warning"
                round
                class="login-form__footer--login mb-10"
              >
                Let's go!
              </p-button>

              <div class="mt-20 login-form__footer--forgot-password">
                <router-link
                  to="/reset"
                  class="link-text"
                >
                  Forgot your password?
                </router-link>
              </div>

              <div class="mt-10 login-form__footer--sign-up">
                <p>Haven't signed up yet?</p>
                <a
                  href="https://gogograndparent.com/pricing-plans"
                  class="link-text"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Register Here
                </a>
              </div>
            </div>
          </el-form>
        </div>
      </div>

      <footer class="footer">
        <p class="footer__copyright">
          &copy; {{ new Date().getFullYear() }} Created with
          <i
            class="gogo-icon-heart"
            aria-hidden="true"
          />
          by GoGo Technologies Inc.
        </p>
      </footer>
      <div class="full-page-background" />
    </div>
  </div>
</template>

<script lang="ts">
import { reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useSwalDialog } from '@/use/swalDialog';
import { sendPost } from '@/Utils/api';
import { emailPattern } from '@/constants/app';
import { useAuth } from '@/use/auth';
import util from '@/Utils/util';

export default {
  setup: () => {
    const router = useRouter();
    const { showWarningPopupDialog } = useSwalDialog();
    const { status, handleAuthResponse, gotoDashboard } = useAuth();

    const user = reactive({
      email: '',
      password: '',
    });

    onMounted(() => {
      const accessToken = localStorage.getItem('access_token') || null;
      const roles = JSON.parse(localStorage.getItem('roles') || '[]');

      if (!accessToken) {
        return;
      }

      gotoDashboard(roles);
    });

    const login = async () => {
      status.resultHtml = '';
      status.loading = true;

      try {
        const res = await sendPost('login', {
          email: user.email,
          password: user.password,
        });

        if (res.data.requiresTwoFactor) {
          localStorage.setItem('requiresTwoFactor', 'true');
          localStorage.setItem('authToken', res.data.authToken);
          router.push('/otp-login');
          return;
        }

        const result = handleAuthResponse(res);
        if (result) {
          const { roles, defaultPage } = result;
          if (defaultPage) {
            await showWarningPopupDialog({
              text: 'Please create a unique password to secure your dashboard account.',
            });
          }
          gotoDashboard(roles, defaultPage);
        }
      } catch (err) {
        status.resultHtml = util.getErrorResponse(err);
      }

      status.loading = false;
    };

    return { login, user, status, emailPattern };
  },
};
</script>

<style lang="scss" scoped>
.login-page {
  font-family: Inter;
  display: flex;
  align-items: center;
  justify-content: center;
  .login-form {
    width: 422px;
    height: 100%;
    padding: 48px;
    gap: 16px;
    border-radius: 12px;
    background: #f2f4fc;
    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      height: 64px;

      &--logo {
        width: 117px;
        height: 21px;
        background-image: url('@/assets/img/logo-gogo-cornflower.svg');
      }

      &--title {
        font-size: 22px;
        font-weight: 700;
        line-height: 24px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #1a1a4c;
        margin: 0;
      }
    }
    &__content {
      label {
        font-size: 16px;
        font-weight: 700;
        line-height: 19.36px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #1a1a4c;
        margin-bottom: 8px;
      }
    }
    &__footer {
      &--login {
        width: 104px;
        height: 40px;
        border-radius: 5px;
        background: #ffc000;
        font-size: 16px !important;
        font-weight: 700;
        line-height: 19.36px;
        text-align: center;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #323776;
      }
      &--forgot-password,
      &--sign-up {
        display: flex;
        flex-direction: row;
        gap: 8px;
        font-size: 16px;
        font-weight: 400;
        line-height: 19.36px;
        text-align: left;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
        color: #090912;
      }
    }
  }
  .full-page-background {
    background-image: url('@/assets/img/login.webp');
  }
  .spinner-class {
    background-color: rgba(106, 102, 99, 0.6) !important;
    .path {
      stroke: #fff;
    }
    .el-loading-spinner .el-loading-text {
      font-size: 16px;
      color: #fff;
    }
  }
  .link-text {
    text-decoration: underline;
    color: #393d94;
  }
}
.footer {
  border-top: none;

  &__copyright {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    color: #ffffff;
  }

  .gogo-icon-heart {
    background-image: url('@/assets/img/gogo-icon-heart.svg');
    height: 12px;
    width: 12px;
    margin: 0 3px;
  }
}
</style>
